import Immutable from 'immutable';

/**
 * @desc breakpoints constants
 */
const BREAKPOINTS = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];

/**
 * Given a layout schema set up a basic interface to read the data
 */
class LayoutSchemaModel {

    /**
     * @constructor
     * @desc Initialize the LayoutSchemaModel from schemas
     * @param {Object} layoutSchema -  the layout schema object
     * @param {Object} layoutComponentMappingSchema - the layout component mapping object
     */
    constructor(layoutSchema, layoutComponentMappingSchema) {
        this.layoutSchema = layoutSchema;
        this.layoutComponentMapping = layoutComponentMappingSchema;
    }

    /**
     * @desc Get a component layout mapping model based on the mapping key
     * @param {String} mappingKey - the identifier of a mapping schema
     * @returns {Object} - mapping model that defines which component goes to which layout area
     */
    getLayoutComponentMapping = (mappingKey) => {
        return this.layoutComponentMapping[mappingKey];
    };

    /**
     * @desc Recursively merge layoutElementSchema if it is extended from another one
     * @param {String} elementName - the name of the layout element schema
     * @returns {Object} - a deepMerged layoutElementSchema
     */
    getMergedSchema = (elementName) => {
        const layoutElementSchema = this.getLayoutElementSchema(elementName);
        if (!layoutElementSchema || !layoutElementSchema.extends) {
            return layoutElementSchema;
        }
        const baseLayoutElementSchema = this.getMergedSchema(layoutElementSchema.extends);
        if (!baseLayoutElementSchema) {
            throw Error(`Can not locate baseLayoutElementSchema for ${layoutElementSchema.extends}.`);
        }
        return Immutable.fromJS(baseLayoutElementSchema).mergeDeep(layoutElementSchema).toJS();
    };

    /**
     * The purpose of this method is to cascade from xl down to xs.
     * @example On the large breakpoint, a layoutSchemaKey of "layoutResultRow" will
     *      check layout_schema for a template matching layoutResultRow_lg.
     *      If it doesn't find one, it checks layoutResultRow_md and cascades down the breakpoints.
     *      If none are found, it just will use layoutResultRow.
     * @param {String} layoutSchemaKey - the desired layout schema's name
     * @param {String} layoutSchemaType - The type of the layoutSchema, either 'mapping' or 'element'
     * @param {String} currentBreakpoint - The current viewport breakpoint from state.
     * @returns {Array} layoutMappingSchema - the correct layout mapping list based on the layout name
     */
    getLayoutMappingWithBreakpoint = (layoutSchemaKey, layoutSchemaType, currentBreakpoint) => {
        // Start the array with the current breakpoint i.e. if we're on md, we don't care about xl/lg breakpoints
        const points = BREAKPOINTS.slice(BREAKPOINTS.indexOf(currentBreakpoint));
        // Get rid of breakpoints that don't exist in the layout model and store the first.
        const largestBreakpoint = points.filter((b) => {
            return this._layoutExists(`${layoutSchemaKey}_${b}`, layoutSchemaType);
        })[0];

        // Appends breakpoint key to layout name e.g. layout_md, layout_xs
        const breakpointExtension = (largestBreakpoint ? `_${largestBreakpoint}` : '');
        return this.getLayoutMapping(layoutSchemaKey + breakpointExtension, layoutSchemaType);
    };

    /**
     * Given a layout name return the appropriate element schema name list
     * @param {String} layoutSchemaKey - the desired layout schema's name
     * @param {String} layoutSchemaType - The type of the layoutSchema, either 'mapping' or 'element'
     * @returns {Array} layoutSchemas - the correct layoutElementSchema name list based on the layout name
     */
    getLayoutMapping = (layoutSchemaKey, layoutSchemaType) => {
        switch (layoutSchemaType) {
            case 'mapping':
                return this.layoutSchema.layoutMappingSchema[layoutSchemaKey];
            case 'element':
                return [layoutSchemaKey];
            default:
                return [];
        }
    };

    /**
     * @desc Given a name return the appropriate layout element schema
     * @param {String} elementName - the name of the layout element schema
     * @returns {Object} - layout element schema before merging
     */
    getLayoutElementSchema = (elementName) => {
        return this.layoutSchema.layoutElementsSchema[elementName];
    };

    /**
     * Determines if a mapping key exists.
     * @param {String} layoutSchemaKey - The layout name to check
     * @param {String} layoutSchemaType - The type of the layoutSchema, either 'mapping' or 'element'
     * @returns {Boolean} - True if the mapping exists, false if not.
     */
    _layoutExists = (layoutSchemaKey, layoutSchemaType) => {
        if (layoutSchemaType === 'element') {
            return !!this.layoutSchema.layoutElementsSchema[layoutSchemaKey];
        }
        return !!this.layoutSchema.layoutMappingSchema[layoutSchemaKey];
    };

    /**
     * Determines if a top-level mapping exists
     * @param {String} schemaName - The name of the mapping schema to check for
     * @returns {Boolean} - True if the mapping exists, false if not.
     */
    layoutMappingExists = (schemaName) => {
        return !!this.layoutComponentMapping[schemaName];
    };

}

export { LayoutSchemaModel };
